import { useState } from "react";
import { Lightbulb, Pencil, Check, X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { cn } from "@/lib/utils";
import type { DiscoveryAssumption } from "@shared/discovery-types";

interface AssumptionEditorProps {
  assumptions: DiscoveryAssumption[];
  onUpdateAssumption: (
    assumptionId: string,
    overrideValue: string | null
  ) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Editor for viewing and overriding extraction assumptions
 *
 * Shows what values were assumed during extraction (e.g., canton inferred
 * from postal code) and allows users to override them if incorrect
 */
export function AssumptionEditor({
  assumptions,
  onUpdateAssumption,
  disabled = false,
  className,
}: AssumptionEditorProps) {
  if (assumptions.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <Lightbulb className="h-4 w-4 text-[var(--warning)]" />
        <span>Assumptions ({assumptions.length})</span>
      </div>
      <p className="text-xs text-muted-foreground">
        These values were inferred during extraction. Review and override if
        needed.
      </p>
      <div className="space-y-2">
        {assumptions.map(assumption => (
          <AssumptionRow
            key={assumption.id}
            assumption={assumption}
            onUpdate={value => onUpdateAssumption(assumption.id, value)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

interface AssumptionRowProps {
  assumption: DiscoveryAssumption;
  onUpdate: (overrideValue: string | null) => void;
  disabled?: boolean;
}

function AssumptionRow({ assumption, onUpdate, disabled }: AssumptionRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(
    assumption.overrideValue || assumption.assumedValue
  );

  const effectiveValue = assumption.overridden
    ? assumption.overrideValue
    : assumption.assumedValue;

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed === assumption.assumedValue) {
      // Reset to original
      onUpdate(null);
    } else {
      onUpdate(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(assumption.overrideValue || assumption.assumedValue);
    setIsEditing(false);
  };

  const handleReset = () => {
    onUpdate(null);
    setEditValue(assumption.assumedValue);
  };

  return (
    <div
      className={cn(
        "rounded-lg border p-3 transition-colors",
        assumption.overridden
          ? "border-[var(--warning)]/30 bg-[var(--warning)]/5"
          : "bg-muted/30"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium">{assumption.fieldLabel}</span>
            <ConfidenceBadge confidence={assumption.confidence} size="sm" />
            {assumption.overridden && (
              <Badge
                variant="outline"
                className="text-xs border-[var(--warning)] text-[var(--warning)]"
              >
                Overridden
              </Badge>
            )}
          </div>

          {isEditing ? (
            <div className="flex items-center gap-2 mt-2">
              <Input
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                className="h-8 flex-1"
                autoFocus
                onKeyDown={e => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
                disabled={disabled}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleSave}
                disabled={disabled}
              >
                <Check className="h-4 w-4 text-[var(--success)]" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={handleCancel}
                disabled={disabled}
              >
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "text-sm",
                  assumption.overridden && "font-medium"
                )}
              >
                {effectiveValue}
              </span>
              {assumption.overridden && (
                <span className="text-xs text-muted-foreground line-through">
                  {assumption.assumedValue}
                </span>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-1">
            {assumption.reason}
          </p>
        </div>

        {!isEditing && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              title="Edit value"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            {assumption.overridden && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleReset}
                disabled={disabled}
                title="Reset to original"
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
